import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * A wrapper validator that applies a given validator to the control only if a related control is considered empty.
 *
 * @param relatedControlName The name of the related control in the form group.
 * @param isEmptyValidator A validator to determine if the related control is empty (returns null if empty).
 * @param ownValidator The validator to apply to this control if the related control is empty.
 * @returns A ValidatorFn for conditional validation.
 *
 * @example
 * const form = new FormGroup({
 *   emails: new FormControl([], {
 *     nonNullable: true,
 *     validators: [
 *       DependentRequiredValidator(
 *         'relatedControl',              // Related control to check
 *         Validators.required,           // How to determine if related control is empty
 *         ObjectRequiredValidator()      // Validator to apply to this control
 *       ),
 *     ]
 *   }),
 *   relatedControl: new FormControl('', Validators.required)
 * });
 */
export function DependentRequiredValidator(
    relatedControlName: string,
    isEmptyValidator: ValidatorFn,
    ownValidator: ValidatorFn,
): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const form = control.parent;

        if (!form) {
            return null;
        }

        const relatedControl = form.get(relatedControlName);

        if (!relatedControl) {
            return null;
        }

        // Check if the related control is empty using the provided emptiness-check validator
        const isRelatedEmpty = isEmptyValidator(relatedControl) !== null;

        // If the related control is empty, apply the ownValidator to this control
        return isRelatedEmpty ? ownValidator(control) : null;
    };
}
